import React, { useEffect, useState } from "react";
import { NAV_LINKS } from "../../data/sparkseed";
import { Menu, X, Sprout } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";

export default function Navbar({ onOpenPitch }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { theme } = useTheme();
  const dark = theme === "dark";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Color tokens depend on theme + scroll state
  const headerBg = scrolled
    ? dark
      ? "bg-[#0A1812]/85 backdrop-blur-md border-b border-[#B8D958]/10"
      : "bg-[#F7F8EF]/90 backdrop-blur-md border-b border-[#14401F]/10"
    : "bg-transparent";

  const logoText = dark ? "text-[#F0EFE6]" : "text-[#0F2818]";
  const logoDot = dark ? "text-[#B8D958]" : "text-[#14401F]";
  const navLinkText = dark
    ? "text-white/75 hover:text-[#B8D958]"
    : "text-[#0F2818]/80 hover:text-[#14401F]";
  const mobileBtn = dark
    ? "border-[#B8D958]/25 text-[#B8D958]"
    : "border-[#14401F]/20 text-[#14401F]";

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${headerBg}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-24 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => goTo("home")}
          className="flex items-center gap-2.5 group"
        >
          <img
            src={dark ? "/sparkseed-logo-stacked-white.png" : "/sparkseed-logo-stacked-clean.png"}
            alt="SparkSeed Ventures"
            className="h-14 lg:h-16 w-auto"
            style={{
              filter: dark
                ? "drop-shadow(0 0 8px rgba(184,217,88,0.25))"
                : "drop-shadow(0 1px 1px rgba(20,64,31,0.08))",
            }}
          />
        </button>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <button
              data-testid={`nav-link-${l.id}`}
              key={l.id}
              onClick={() => goTo(l.id)}
              className={`px-3.5 py-2 text-sm font-medium link-underline transition-colors whitespace-nowrap ${navLinkText}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-pitch-cta"
            onClick={onOpenPitch}
            className="hidden sm:inline-flex items-center gap-2 bg-[#B8D958] text-[#0F2818] hover:bg-[#a8c948] font-semibold rounded-full px-5 py-2.5 text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
          >
            Pitch to Us
            <span className="inline-block">→</span>
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className={`lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-full border transition-colors ${mobileBtn}`}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className={`lg:hidden px-6 py-4 ${dark ? "bg-[#0A1812] border-t border-[#B8D958]/10" : "bg-[#F7F8EF] border-t border-[#14401F]/10"}`}>
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => goTo(l.id)}
                className={`text-left py-3 px-2 border-b ${dark ? "text-[#F0EFE6] border-[#B8D958]/8" : "text-[#0F2818] border-[#14401F]/5"}`}
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => { setOpen(false); onOpenPitch(); }}
              className="mt-3 bg-[#B8D958] text-[#0F2818] font-semibold rounded-full px-5 py-3"
            >
              Pitch to Us
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
